import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LoadingBar } from './MyAccountLoadingStates';
import MyAccountCard from './MyAccountCard';

interface OrderItem {
    name: string;
    qty: number;
}

interface OrderActionsData {
    order_id: number;
    order_number: string;
    items: OrderItem[];
    tracking_number: string;
    tracking_link: string;
    reorder_url: string;
}

let inFlightOrderActionsRequest: Promise<void> | null = null;
let orderActionsFailureCount = 0;
let orderActionsLastFailedAt = 0;
const ORDER_ACTIONS_MAX_FAILURES = 5;
const ORDER_ACTIONS_FAILURE_COOLDOWN_MS = 10000;

const OrderActions: React.FC = () => {
    const [data, setData] = useState<OrderActionsData | null>(null);
    const [loading, setLoading] = useState(true);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        loadOrderActions();
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const loadOrderActions = async () => {
        const now = Date.now();
        if (
            orderActionsFailureCount >= ORDER_ACTIONS_MAX_FAILURES &&
            now - orderActionsLastFailedAt < ORDER_ACTIONS_FAILURE_COOLDOWN_MS
        ) {
            if (isMountedRef.current) {
                setLoading(false);
            }
            return;
        }

        if (inFlightOrderActionsRequest) {
            await inFlightOrderActionsRequest;
            return;
        }

        inFlightOrderActionsRequest = (async () => {
        try {
            const response = await fetch('/wp-admin/admin-ajax.php?action=get_order_actions');
            
            // Log response details for debugging
            const contentType = response.headers.get('content-type');
            console.log('OrderActions response content-type:', contentType);
            
            if (!response.ok) {
                const text = await response.text();
                console.error('OrderActions HTTP error:', response.status, text);
                throw new Error(`Failed to load order actions: ${response.status}`);
            }
            
            // Check if response is JSON
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('OrderActions non-JSON response:', text.substring(0, 500));
                throw new Error('Server returned non-JSON response');
            }
            
            const result = await response.json();
            if (result.success) {
                orderActionsFailureCount = 0;
                if (isMountedRef.current) {
                    setData(result.data);
                }
            } else {
                orderActionsFailureCount += 1;
                orderActionsLastFailedAt = Date.now();
                console.error('OrderActions API error:', result);
            }
        } catch (error) {
            orderActionsFailureCount += 1;
            orderActionsLastFailedAt = Date.now();
            console.error('Error loading order actions:', error);
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
            inFlightOrderActionsRequest = null;
        }
        })();

        await inFlightOrderActionsRequest;
    };

    if (loading) {
        return (
            <MyAccountCard slug="order-actions" title="Order Actions" className="order-actions mt-2">
                <p style={{ marginBottom: 0, fontSize: '.8rem', color: '#aaa' }}>ORDER#</p>
                <LoadingBar height="28px" width="72%" style={{ marginBottom: '12px' }} />
                <p style={{ marginBottom: 0, fontSize: '.8rem', color: '#aaa' }}>DETAILS</p>
                <div className="order-items-column" style={{ marginBottom: '14px' }}>
                    <LoadingBar height="16px" width="84%" style={{ marginBottom: '8px' }} />
                    <LoadingBar height="16px" width="76%" style={{ marginBottom: '8px' }} />
                    <LoadingBar height="16px" width="68%" />
                </div>
                <p style={{ marginBottom: 0, fontSize: '.8rem', color: '#aaa', marginTop: '15px' }}>TRACKING No.</p>
                <LoadingBar height="16px" width="58%" style={{ marginBottom: '16px' }} />
                <div className="button-column" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '15px' }}>
                    <LoadingBar height="38px" width="100%" style={{ borderRadius: '8px' }} />
                    <LoadingBar height="38px" width="100%" style={{ borderRadius: '8px' }} />
                    <LoadingBar height="38px" width="100%" style={{ borderRadius: '8px' }} />
                </div>
            </MyAccountCard>
        );
    }

    if (!data || !data.order_id) {
        return (
            <MyAccountCard slug="order-actions" title="Order Actions" className="order-actions mt-2">
                <p>No recent orders found.</p>
            </MyAccountCard>
        );
    }

    return (
        <MyAccountCard slug="order-actions" title="Order Actions" className="order-actions mt-2">
            <p style={{ marginBottom: 0, fontSize: '.8rem', color: '#aaa' }}>ORDER#</p>
            <p className="no-margin highlight-text large-text primary-color-font">
                {data.order_number}
            </p>
            <p style={{ marginBottom: 0, fontSize: '.8rem', color: '#aaa' }}>DETAILS</p>
            <div className="order-items-column">
                {data.items.map((item, index) => (
                    <p key={index} style={{ margin: '5px 0' }}>
                        {item.name} x {item.qty}
                    </p>
                ))}
            </div>
            {data.tracking_number && (
                <>
                    <p style={{ marginBottom: 0, fontSize: '.8rem', color: '#aaa', marginTop: '15px' }}>
                        TRACKING No.
                    </p>
                    <p>{data.tracking_number}</p>
                </>
            )}
            <div className="button-column" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '15px' }}>
                {data.tracking_link && (
                    <a className="button weka-primary-btn" href={data.tracking_link} target="_blank" rel="noopener noreferrer">
                        TRACK ORDER
                    </a>
                )}
                {data.reorder_url && (
                    <a className="button weka-primary-btn" href={data.reorder_url}>
                        RE ORDER
                    </a>
                )}
                <Link className="button weka-secondary-btn" to={`/view-order/${data.order_id}`}>
                    VIEW ORDER
                </Link>
            </div>
        </MyAccountCard>
    );
};

export default OrderActions;
